"use client";

import { cn } from "@/lib/cn";
import { countdownTag } from "@/lib/smartico/countdown";
import { rewardView, type AccountReward, type RewardAction } from "@/lib/api/rewards";
import type { Bonus } from "@/lib/api/incentives";
import type { TokenScale } from "@/lib/format/amount";
import { WageringStrip } from "@/components/casino/BonusModal";
import { Button, Chip, LinkButton } from "@/components/ui/primitives";
import { Icon, type IconName } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";

/* One reward, on the kit's `rewards-*` item frames: the reward's own art in a
   dark square, the title over the value in the success colour, a chip row —
   the type, then a countdown that turns urgent-red near expiry — and the one
   action the reward's state allows. A reward mid wagering adds the kit's
   gradient strip underneath with what remains, stated as a figure rather than
   a fill: the tranche does not say the target, and an invented percentage is
   worse than none.

   Media follows the whitelabel's order: the bonus's image, else its icon, else
   the type's own glyph. A bonus configured with neither still gets a real icon
   — which is the default this card was missing. The title is the bonus's name
   when the reward names one; the type label is the fallback, not the rule. */

const TYPE_ICON: Record<string, IconName> = {
  free_spin: "sparkles",
  free_money: "gift",
  cashback: "coins",
  rakeback: "history",
  bounty: "target",
  tier_assignment: "trophy",
};

export function RewardCard({
  reward,
  now,
  scale,
  bonus,
  gameName,
  progress,
  action,
  onClaim,
  onOpen,
}: {
  reward: AccountReward;
  now: number;
  scale?: TokenScale & { symbol: string };
  bonus?: Bonus;
  gameName?: string;
  progress?: { percent: number; note: string } | null;
  action: RewardAction;
  onClaim: () => void;
  onOpen: () => void;
}) {
  const view = rewardView(reward, now, scale);
  const countdown =
    !view.isExpired && reward.expiresAt ? countdownTag(Date.parse(reward.expiresAt), now) : null;
  /* Every wagering reward gets the strip. rewardView's detail only speaks for
     free spins; a wagering cashback says nothing there, and gating on it hid
     the strip from exactly the rewards whose rollup had a measured bar. */
  const wagering = reward.status === "wagering";
  const title = bonus?.name ?? view.title;

  return (
    <li className="flex flex-col gap-[10px] rounded-lg bg-elevation-2 p-[12px]">
      <div className="flex items-center gap-[12px]">
        <RewardMedia type={reward.type} bonus={bonus} title={title} />

        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            aria-haspopup="dialog"
            className="block max-w-full cursor-pointer truncate text-left text-[14px] font-bold leading-[1.6] text-fg hover:text-brand-fg-accent"
          >
            {title}
          </button>
          <p
            className={cn(
              "text-[12px] font-black leading-[1.5] tabular-nums",
              view.isExpired ? "text-fg-subtle" : "text-success-fg",
            )}
          >
            {view.value}
          </p>
          <div className="mt-[4px] flex flex-wrap gap-[4px]">
            {bonus && <Chip className="bg-elevation-3">{view.title}</Chip>}
            {view.detail && !wagering && <Chip className="bg-elevation-3">{view.detail}</Chip>}
            {countdown && (
              <Chip
                className={cn(
                  "tabular-nums",
                  countdown.isUrgent ? "bg-error text-white" : "bg-elevation-3",
                )}
              >
                {countdown.label}
              </Chip>
            )}
            {view.isExpired && <Chip className="bg-elevation-3 text-fg-subtle">Expired</Chip>}
            {gameName && <Chip className="max-w-[140px] truncate bg-elevation-3">{gameName}</Chip>}
          </div>
        </div>

        <RewardActionButton action={action} onClaim={onClaim} />
      </div>

      {wagering &&
        (progress ? (
          <WageringStrip note={progress.note} percent={progress.percent} />
        ) : (
          <WageringStrip note={view.detail as string} />
        ))}
    </li>
  );
}

/* The 40px square: the bonus's image covers it, its icon sits inside it, and
   the type glyph is what a bonus with neither gets. RemoteImage fills its
   parent absolutely, so the square is the positioning box. */
function RewardMedia({ type, bonus, title }: { type: string; bonus?: Bonus; title: string }) {
  const imageUrl = bonus?.imageUrl ?? null;
  const iconUrl = bonus?.iconUrl ?? null;

  return (
    <span className="relative grid h-[40px] w-[40px] shrink-0 place-items-center overflow-hidden rounded-md bg-elevation-3 text-fg-subtle">
      {imageUrl ? (
        <RemoteImage src={imageUrl} alt={title} width={40} height={40} />
      ) : iconUrl ? (
        <RemoteImage src={iconUrl} alt={title} width={40} height={40} className="object-contain p-[6px]" />
      ) : (
        <Icon name={TYPE_ICON[type] ?? "gift"} size={18} />
      )}
    </span>
  );
}

/* The whitelabel's RewardActionButton: a Claim fires the claim, a Play is a
   link to the game, and everything else is a disabled label that says what the
   reward is doing instead. */
export function RewardActionButton({
  action,
  onClaim,
  className,
}: {
  action: RewardAction;
  onClaim: () => void;
  className?: string;
}) {
  if (action.kind === "claim") {
    return (
      <Button size="sm" onClick={onClaim} className={cn("shrink-0", className)}>
        {action.label}
      </Button>
    );
  }

  if (action.kind === "play") {
    return (
      <LinkButton
        href={action.href}
        variant="primary"
        size="sm"
        icon="play"
        className={cn("shrink-0", className)}
      >
        {action.label}
      </LinkButton>
    );
  }

  return (
    <Button size="sm" variant="outline" disabled className={cn("shrink-0", className)}>
      {action.label}
    </Button>
  );
}
